import React, {
    FC,
    KeyboardEventHandler,
    ReactNode,
    useEffect,
    useRef,
} from 'react';

import { Stack } from '../../../Stack';
import { Header } from './Header';
import { Item } from './Item';
import type { ItemProps } from './Item';
import styles from './index.module.scss';


type ListProps = Pick<ItemProps, 'onChange' | 'selected'> & {
    /** Заголовок списка
     * @example Час
     * @example Мин.
     */
    header: ReactNode;
    /** Список элементов для выбора */
    options: number[];
    /** Элемент для стартовой позиции скролла.
     * Например, необходимо показать для начала 13 часов, а не 00 */
    defaultScrollStartElement?: number;
};

/** Изменение фокуса элемента при нажатии на клавиши 'ArrowUp' и 'ArrowDown' */
const onKeyDownCallback: KeyboardEventHandler = e => {
    const element = e.target as HTMLDivElement;
    let focusElement: Element;

    if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (element.previousElementSibling)
            focusElement = element.previousElementSibling;
        else
            focusElement = element.parentElement.lastElementChild;
    }
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (element.nextElementSibling)
            focusElement = element.nextElementSibling;
        else
            focusElement = element.parentElement.firstElementChild;
    }

    const htmlFocusElement = focusElement as HTMLElement;
    htmlFocusElement?.focus({
        preventScroll: false,
    });
};

const useScrollToStartElement = (elementNumber: number) => {
    const ref = useRef<HTMLDivElement>();
    useEffect(() => {
        if (ref.current && elementNumber) {
            const htmlElement = ref.current.children.item(elementNumber) as HTMLElement;
            htmlElement.scrollIntoView();
        }
    }, [elementNumber]);
    return ref;
};

export const List: FC<ListProps> = ({
    header,
    options,
    selected,
    onChange,
    defaultScrollStartElement,
}) => {
    const listRef = useScrollToStartElement(defaultScrollStartElement);
    return (
        <Stack spacing={2}>
            <Header>{header}</Header>
            <Stack
                ref={listRef}
                spacing={2}
                className={styles.updateScrollClass}
                onKeyDown={onKeyDownCallback}
            >
                {options.map((v, index) => (
                    <Item
                        key={v}
                        value={v}
                        selected={selected}
                        onChange={onChange}
                        needTabIndex={
                            index === (defaultScrollStartElement || 0)
                        }
                    />
                ))}
            </Stack>
        </Stack>
    );
};
